import { type Objectish, enablePatches } from "immer";
import { fromPromise } from "xstate";

import { type StateTransaction } from "../api-canvas-metadata/StateTransaction";
import { type ResolveTransactionOutput } from "../state-sync-server/StateSyncServerMachine";
import { type StateSyncServerContext } from "./StateSyncServerContext";

// Required in v6.
enablePatches();

export interface ProcessTransactionInput<TData extends Objectish> {
	context: StateSyncServerContext<TData>;
}

export type ProcessTransactionOutput<TData extends Objectish> = StateSyncServerContext<TData>;

const MAX_TRANSACTION_BUFFER_SIZE = 1000;

export function makeProcessTransactionActor<TData extends Objectish>(
	resolveTransaction: (
		transaction: StateTransaction,
		input: TData,
		version: number
	) => Promise<ResolveTransactionOutput<TData>>,
	validateTransaction:
		| ((transaction: StateTransaction, input: TData, result: unknown) => boolean)
		| undefined
) {
	return fromPromise<ProcessTransactionOutput<TData>, ProcessTransactionInput<TData>>(
		async ({ input: { context } }) => {
			const [activeTransaction, ...newTransactionQueue] = context.transactionQueue;
			if (!activeTransaction) {
				return { ...context, lastTransactionProcessed: null };
			}

			// Active transaction is based off of a stale version.
			// Likely could cause conflicts, resolve by rejecting the transaction.
			if (activeTransaction.lastVersionSeen !== context.version) {
				return {
					data: context.data,
					version: context.version,

					transactionProcessingPaused: context.transactionProcessingPaused,
					transactionQueue: newTransactionQueue,
					lastTransactionProcessed: {
						type: "error",
						error: "conflict",
						message: `Transactions can only be applied when client's lastVersionSeen (${activeTransaction.lastVersionSeen}) matches server's current version (${context.version})`,
						transaction: activeTransaction,
					},
					transactionHistoryBuffer: context.transactionHistoryBuffer,
					isPersistenceFailing: context.isPersistenceFailing,
				};
			}
			const { stateTransaction, resolvedData } = await resolveTransaction(
				activeTransaction,
				context.data,
				context.version
			);
			const newData = resolvedData;
			if (
				validateTransaction &&
				!validateTransaction(stateTransaction, context.data, newData)
			) {
				return {
					data: context.data,
					version: context.version,

					transactionProcessingPaused: context.transactionProcessingPaused,
					transactionQueue: newTransactionQueue,
					lastTransactionProcessed: {
						type: "error",
						error: "validation-failure",
						message: `Transaction produced invalid output`,
						transaction: stateTransaction,
					},
					transactionHistoryBuffer: context.transactionHistoryBuffer,
					isPersistenceFailing: context.isPersistenceFailing,
				};
			}
			const transactionHistoryBuffer =
				context.transactionHistoryBuffer.length < MAX_TRANSACTION_BUFFER_SIZE
					? [...context.transactionHistoryBuffer, stateTransaction]
					: [...context.transactionHistoryBuffer.slice(1), stateTransaction];

			return {
				data: newData as TData,
				version: context.version + stateTransaction.patches.length,

				transactionProcessingPaused: context.transactionProcessingPaused,
				transactionQueue: newTransactionQueue,
				lastTransactionProcessed: {
					type: "success",
					transaction: stateTransaction,
				},
				transactionHistoryBuffer,
				isPersistenceFailing: context.isPersistenceFailing,
			};
		}
	);
}
